import { DateRangeParam } from '@/Api';
import { AppUserSettingNameType, DateFormatType } from '@/Enums';
import { UserSettingModel } from '@/Models';
import moment from 'moment-timezone';
import { Moment } from 'moment';

/* ---- Default settings ---- */
function setDefaultSettings(settings?: { [key in AppUserSettingNameType]?: UserSettingModel }) {
  const timeZoneSetting = settings?.[AppUserSettingNameType.TimeZone];
  const dateFormatSetting = settings?.[AppUserSettingNameType.DateFormat];
  const timeFormatSetting = settings?.[AppUserSettingNameType.TimeFormat];
  const startOfWeekSetting = settings?.[AppUserSettingNameType.FirstDayOfWeek];

  if (timeZoneSetting) {
    moment.tz.setDefault(timeZoneSetting.value);
  } // if

  moment.updateLocale('en', {
    week: {
      dow: Number(startOfWeekSetting?.value ?? 1),
    },
    longDateFormat: {
      LT: 'h:mm A', // default
      LTS: 'h:mm:ss A', // default
      L: 'MM/DD/YYYY', // default
      LL: 'YYYY-MM-DD HH:mm:ss', // server setting
      LLL: dateFormatSetting?.value ?? 'DD.MM.YYYY', // user setting or default value
      LLLL: `${dateFormatSetting?.value ?? 'DD.MM.YYYY'} ${timeFormatSetting?.value ?? 'hh:mm:ss'}`, // user setting or default value
    },
  });
} // setDefaultSettings
/* -------------------------- */

/* ---- Public moment to date converters ---- */
function momentObjectToDateObject(date: Moment): Date {
  const m = moment(date);
  return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
}

/* ---- Public date to string converters ---- */
function toFormattedString(date: Date, dateFormat: DateFormatType): string {
  return moment(date).local().format(dateFormat);
}

function toFormattedUtcString(date: Date): string {
  return moment.utc(date).format(DateFormatType.SERVER_DATE_TIME);
}

function changeDateFormat(date: string | undefined, oldFormat: DateFormatType, newFormat: DateFormatType): string {
  if (!date) return '';
  return moment(date, oldFormat).format(newFormat);
}

function convertTimeToSeconds(date: string): number {
  return moment(date, 'hh:mm:ss').diff(moment().startOf('day'), 'seconds');
}

/* ---- Public date range converters ---- */
export function createHoursRange(startHour: string, endHour: string, tz = moment.tz.guess()) {
  const start = moment.tz(startHour, 'HH:mm', tz).toDate();
  const end = moment.tz(endHour, 'HH:mm', tz).toDate();

  return [start, end];
}

function convertDateToHours(date: Date, isShortTime = false): string {
  const originalOffset = date.getTimezoneOffset();
  const format = isShortTime ? 'HH:mm' : 'HH:mm:ss';

  return moment(date).zone(originalOffset).format(format);
}

function convertHoursToDate(date: string, tz = moment.tz.guess()): Date {
  return moment.tz(date, 'HH:mm:ss', tz).toDate();
}
/* ------------------------------------------ */

/* ---- Public string converters ---- */
function localStringToServerString(localString: string | undefined, isShortDate = false): string {
  if (!localString) return '';

  return moment(localString, isShortDate ? DateFormatType.LOCAL_DATE : DateFormatType.LOCAL_DATE_TIME).format(
    DateFormatType.SERVER_DATE_TIME,
  );
}

function serverStringToLocalString(serverString: string | undefined, isShortDate = false): string {
  if (!serverString) return '';

  return moment(serverString, DateFormatType.SERVER_DATE_TIME).format(
    isShortDate ? DateFormatType.LOCAL_DATE : DateFormatType.LOCAL_DATE_TIME,
  );
}

function localStringToUtcString(localString: string | undefined, isShortDate = false): string {
  if (!localString) return '';

  return moment(localString, isShortDate ? DateFormatType.LOCAL_DATE : DateFormatType.LOCAL_DATE_TIME)
    .utc()
    .format(DateFormatType.SERVER_DATE_TIME);
} // localStringToUtcString

function utcStringToLocalString(utcString: string | null | undefined, isShortDate = false): string {
  const timeZone = moment().tz();

  if (!utcString || !timeZone) return '';

  return moment
    .utc(utcString, DateFormatType.SERVER_DATE_TIME)
    .tz(timeZone)
    .format(isShortDate ? DateFormatType.LOCAL_DATE : DateFormatType.LOCAL_DATE_TIME);
} // utcStringToLocalString

function localDateRangeToUtcString(dateRange: DateRangeParam): DateRangeParam {
  return {
    ...dateRange,
    timeFrom: localStringToUtcString(dateRange.timeFrom),
    timeTo: localStringToUtcString(dateRange.timeTo),
  };
} // localDateRangeToUtcString

function getEndOfDateString(dateString: string | undefined): string {
  if (!dateString) return '';

  return moment(dateString, DateFormatType.LOCAL_DATE).endOf('day').format(DateFormatType.LOCAL_DATE_TIME);
}

function getCurrentDateTime(dateFormat: DateFormatType): string {
  return moment().format(dateFormat);
}

function getCountOfDaysBetweenDates(start: string, end: string, isFormatted?: boolean): number {
  const startDate = moment(isFormatted ? start : localStringToUtcString(start));
  const endDate = moment(isFormatted ? end : localStringToUtcString(end));

  return endDate.diff(startDate, 'days');
}

/* ---------------------------------- */

export const dateTimeUtils = {
  setDefaultSettings,
  momentObjectToDateObject,

  utcStringToLocalString,
  localStringToUtcString,
  localStringToServerString,
  serverStringToLocalString,
  localDateRangeToUtcString,
  toFormattedString,
  toFormattedUtcString,
  changeDateFormat,
  convertTimeToSeconds,
  convertDateToHours,
  convertHoursToDate,

  createHoursRange,
  getEndOfDateString,
  getCurrentDateTime,
  getCountOfDaysBetweenDates,
};
